import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { XIcon, HeartIcon, Share2Icon, DownloadIcon, ExternalLinkIcon, UserIcon, TagIcon, CheckCircleIcon, AlertCircleIcon, BookOpenIcon, MailIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useRoleTheme } from '../../hooks/useRoleTheme';
import SaveToFavorites from '../UI/SaveToFavorites';
import { DisclosureBadgeGroup } from '../Disclosure/DisclosureBadgeGroup';
import { UPLOADER_ROLE_LABELS, type GraphicUploaderRole } from './PromotionalGraphic';
import type { DisclosureId } from '../../data/disclosures';
export interface GraphicDetailCardProps {
  // Image
  imageUrl: string;
  title: string;
  // Creator info
  creator?: string;
  creatorProfileUrl?: string;
  // Author info (for book graphics)
  authorName?: string;
  authorId?: string;
  // Book/product info
  bookTitle?: string;
  bookId?: string;
  // Metadata
  type?: string; // 'tropes', 'spice level', etc.
  serviceType?: string;
  description?: string;
  tags?: string[];
  genre?: string[];
  // Pricing (for gallery items)
  price?: number;
  priceType?: 'fixed' | 'starting' | 'commissioned';
  // Navigation
  onClose?: () => void;
  onPrev?: () => void;
  onNext?: () => void;
  itemIndex?: number;
  totalItems?: number;
  // Actions
  onDownload?: () => void;
  onShare?: () => void;
  // Commercial disclosure — full set with explanations, unlike the card overlay
  uploaderRole?: GraphicUploaderRole;
  uploaderName?: string;
  disclosures?: DisclosureId[];
}
const GraphicDetailCard: React.FC<GraphicDetailCardProps> = ({
  id,
  imageUrl,
  title,
  creator,
  creatorProfileUrl,
  authorName,
  authorId,
  bookTitle,
  bookId,
  type,
  serviceType,
  description,
  tags,
  genre,
  price,
  priceType,
  onClose,
  onPrev,
  onNext,
  itemIndex,
  totalItems,
  onDownload,
  onShare,
  uploaderRole,
  uploaderName,
  disclosures
}) => {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const [isSaved, setIsSaved] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  // Prevent scrolling when open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && onPrev) onPrev();
      if (e.key === 'ArrowRight' && onNext) onNext();
    };
    window.addEventListener('keydown', handleKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKey);
    };
  }, [onClose, onPrev, onNext]);
  // Format price display
  const formatPrice = () => {
    if (!price) return null;
    if (priceType === 'starting') return `From $${price}`;
    if (priceType === 'commissioned') return `Comm: $${price}`;
    return `$${price}`;
  };
  const displayType = type || serviceType;
  return <AnimatePresence data-id="element-1632" data-mp="30t7bt4m5dupt">
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6" data-id="element-1633" data-mp="30p9pyb86qp28">
        {/* Backdrop */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} data-id="element-1634" data-mp="2o90kmzwjn3zs" />

        {/* Card */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        duration: 0.3,
        type: 'spring',
        damping: 25,
        stiffness: 300
      }} className={`relative w-full max-w-2xl max-h-[90vh] flex flex-col rounded-2xl shadow-2xl overflow-hidden ${dk.isDark ? 'bg-gray-900 border border-gray-700' : 'bg-white'}`} onClick={e => e.stopPropagation()} data-id="element-1635" data-mp="29i1dltbcwnib">
          {/* Close Button */}
          <button onClick={onClose} className="absolute top-4 right-4 z-10 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors backdrop-blur-md" data-id="element-1636" data-mp="y3b9u1n0zkt1">
            <XIcon className="w-5 h-5" data-id="element-1637" data-mp="17ufn8wx1yxlr" />
          </button>

          {/* Navigation Arrows (Desktop) */}
          {onPrev && <button onClick={onPrev} className="absolute left-4 top-1/2 -translate-y-1/2 z-10 p-3 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors backdrop-blur-md hidden md:flex" data-id="element-1638" data-mp="1cr61deparj4j">
              <ChevronLeftIcon className="w-6 h-6" data-id="element-1639" data-mp="3jj6y2rnbibvv" />
            </button>}
          {onNext && <button onClick={onNext} className="absolute right-4 top-1/2 -translate-y-1/2 z-10 p-3 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors backdrop-blur-md hidden md:flex" data-id="element-1640" data-mp="3nkir775ehf8t">
              <ChevronRightIcon className="w-6 h-6" data-id="element-1641" data-mp="3b8zv06r2swlp" />
            </button>}

          {/* Image Section */}
          <div className="relative flex-shrink-0 bg-black/5 flex items-center justify-center min-h-[200px] max-h-[50vh]" data-id="element-1642" data-mp="369dhdw7c5ecd">
            <img src={imageUrl} alt={title} className="w-full h-full object-contain max-h-[50vh]" data-id="element-1643" data-mp="2uherhqzear0" />

            {/* Counter Badge */}
            {itemIndex !== undefined && totalItems !== undefined && <div className="absolute bottom-4 right-4 px-3 py-1 rounded-full bg-black/60 text-white text-xs font-medium backdrop-blur-md" data-id="element-1644" data-mp="3pxm6tyhhv686">
                {itemIndex + 1} / {totalItems}
              </div>}
          </div>

          {/* Details Section */}
          <div className="flex-1 overflow-y-auto p-6" data-id="element-1645" data-mp="29k5qe8ny6d12">
            <div className="flex flex-col gap-6" data-id="element-1646" data-mp="2b2cu4uxn4ujx">
              {/* Header Info */}
              <div data-id="element-1647" data-mp="2b09rnbfax8yb">
                <div className="flex items-start justify-between gap-4" data-id="element-1648" data-mp="2g1fqlqzk1sga">
                  <div data-id="element-1649" data-mp="2fzcyn1eudp8x">
                    {displayType && <span className={`inline-block px-2.5 py-1 ${rt.badgeRadius} text-xs font-semibold mb-2 uppercase tracking-wide ${dk.isDark ? 'bg-indigo-900/50 text-indigo-300' : 'bg-indigo-100 text-indigo-800'}`} data-id="element-1650" data-mp="220wi6ir6gkdl">
                        {displayType}
                      </span>}
                    <h2 className={`text-2xl font-bold leading-tight ${dk.isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-1651" data-mp="1idp5qqhlhp25">
                      {title}
                    </h2>
                  </div>
                  {price && <div className={`flex-shrink-0 px-3 py-1.5 rounded-lg font-bold text-sm ${dk.isDark ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-900'}`} data-id="element-1652" data-mp="19dshgpz4as7m">
                      {formatPrice()}
                    </div>}
                </div>

                {/* Creator / Author Links */}
                <div className="flex flex-wrap gap-y-2 gap-x-6 mt-3 text-sm" data-id="element-1653" data-mp="13s1hat4czydh">
                  {(creator || creatorProfileUrl) && <div className="flex items-center gap-1.5" data-id="element-1654" data-mp="zo3hshb4gljw">
                      <span className={dk.isDark ? 'text-gray-400' : 'text-gray-500'} data-id="element-1655" data-mp="1do4i9vnh5qyu">
                        Created by
                      </span>
                      {creatorProfileUrl ? <Link to={creatorProfileUrl} className={`font-medium hover:underline ${dk.isDark ? 'text-indigo-400' : 'text-indigo-600'}`} data-id="element-1656" data-mp="1igxn0jiy0zax">
                          {creator || 'Unknown'}
                        </Link> : <span className={`font-medium ${dk.isDark ? 'text-gray-200' : 'text-gray-900'}`} data-id="element-1657" data-mp="2ysqo1ywi2jnh">
                          {creator}
                        </span>}
                    </div>}

                  {(authorName || authorId) && <div className="flex items-center gap-1.5" data-id="element-1658" data-mp="2g326d7hnszq2">
                      <span className={dk.isDark ? 'text-gray-400' : 'text-gray-500'} data-id="element-1659" data-mp="2g0fpgh8vp7v6">
                        For author
                      </span>
                      {authorId ? <Link to={`/author/${authorId}`} className={`font-medium hover:underline ${dk.isDark ? 'text-indigo-400' : 'text-indigo-600'}`} data-id="element-1660" data-mp="2tv8bzzn6rob9">
                          {authorName}
                        </Link> : <span className={`font-medium ${dk.isDark ? 'text-gray-200' : 'text-gray-900'}`} data-id="element-1661" data-mp="1ov0kxhyefwku">
                          {authorName}
                        </span>}
                    </div>}

                  {uploaderRole && <div className="flex items-center gap-1.5" data-id="element-1662" data-mp="1aviw52l3sjbh">
                      <span className={dk.isDark ? 'text-gray-400' : 'text-gray-500'} data-id="element-1663" data-mp="164iyfv45tx7x">
                        Posted by
                      </span>
                      <span className={`font-medium ${dk.isDark ? 'text-gray-200' : 'text-gray-900'}`} data-id="element-1664" data-mp="w7nx74gi5ipy">
                        {uploaderName || 'Unknown'} ·{' '}
                        {UPLOADER_ROLE_LABELS[uploaderRole]}
                      </span>
                    </div>}
                </div>

                {/* Full disclosure set with tap/hover explanations. PR work is
                    paid and legitimate — the label informs, it does not
                    penalise, and it never affects organic placement. */}
                <DisclosureBadgeGroup disclosures={disclosures} subjectType="graphic" maxVisible={4} className="mt-3" data-id="element-1665" data-mp="167194kqcxgrl" />
              </div>

              {/* Book Link */}
              {(bookTitle || bookId) && <div className={`p-4 rounded-xl flex items-center justify-between ${dk.isDark ? 'bg-gray-800/50' : 'bg-gray-50'}`} data-id="element-1666" data-mp="sfct46czm85c">
                  <div className="flex items-center gap-3" data-id="element-1667" data-mp="23t8kqel2w9dg">
                    <div className={`p-2 rounded-lg ${dk.isDark ? 'bg-gray-700' : 'bg-white shadow-sm'}`} data-id="element-1668" data-mp="23vuqq9sbmgtk">
                      <BookOpenIcon className={`w-5 h-5 ${dk.isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-1669" data-mp="26dwrk1qv1caw" />
                    </div>
                    <div data-id="element-1670" data-mp="1xdrp860ix43l">
                      <p className={`text-xs font-medium uppercase tracking-wide ${dk.isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-1671" data-mp="1xeji392qknq6">
                        Associated Book
                      </p>
                      {bookId ? <Link to={`/book/${bookId}`} className={`font-semibold hover:underline ${dk.isDark ? 'text-indigo-400' : 'text-indigo-600'}`} data-id="element-1672" data-mp="2u4afbg2ewozo">
                          {bookTitle}
                        </Link> : <p className={`font-semibold ${dk.isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-1673" data-mp="382qjaz8w41e4">
                          {bookTitle}
                        </p>}
                    </div>
                  </div>
                </div>}

              {/* Description */}
              {description && <div data-id="element-1674" data-mp="3do98j9r35zzp">
                  <h3 className={`text-sm font-semibold uppercase tracking-wide mb-2 ${dk.isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-1675" data-mp="3dnpqzu5cldm7">
                    Description
                  </h3>
                  <p className={`text-sm leading-relaxed ${dk.isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-1676" data-mp="2z0o7dldruauf">
                    {description}
                  </p>
                </div>}

              {/* Tags & Genres */}
              {(tags?.length || genre?.length) && <div className="space-y-3" data-id="element-1677" data-mp="2l151z0cchdlt">
                  {genre && genre.length > 0 && <div className="flex flex-wrap gap-2" data-id="element-1678" data-mp="2g1ilmi17zh8s">
                      {genre.map(g => <span key={g} className={`px-2.5 py-1 rounded-md text-xs font-medium ${dk.isDark ? 'bg-blue-900/30 text-blue-300 border border-blue-800' : 'bg-blue-50 text-blue-700 border border-blue-100'}`} data-id="element-1679" data-mp="25wttnyzfyyuu">
                          {g}
                        </span>)}
                    </div>}

                  {tags && tags.length > 0 && <div className="flex flex-wrap gap-2" data-id="element-1680" data-mp="1s674axfkmlbf">
                      {tags.map(tag => <span key={tag} className={`px-2.5 py-1 ${rt.badgeRadius} text-xs ${dk.isDark ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-600'}`} data-id="element-1681" data-mp="1x50a03om9811">
                          #{tag}
                        </span>)}
                    </div>}
                </div>}

              {/* Action Buttons */}
              <div className={`flex gap-3 pt-4 border-t ${dk.isDark ? 'border-gray-800' : 'border-gray-100'}`} data-id="element-1682" data-mp="3b5ejmlnykpxh">
                {/* View Profile - primary CTA */}
                <div className="flex-1" data-id="element-1683" data-mp="3kwizropdegtw">
                  {creatorProfileUrl ? <Link to={creatorProfileUrl} className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-colors bg-indigo-600 hover:bg-indigo-700 text-white`} data-id="element-1684" data-mp="3g3g1x4dk8l7w">
                      <UserIcon className="w-4 h-4" data-id="element-1685" data-mp="7oo85r9lxkoe" />
                      View Profile
                    </Link> : authorId ? <Link to={`/author/${authorId}`} className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-colors bg-indigo-600 hover:bg-indigo-700 text-white`} data-id="element-1686" data-mp="2n8mpy3ipc5h">
                      <UserIcon className="w-4 h-4" data-id="element-1687" data-mp="jz5rqir9p8ro" />
                      View Profile
                    </Link> : <button className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-colors bg-indigo-600 hover:bg-indigo-700 text-white`} data-id="element-1688" data-mp="a8ubbwqed73w">
                      <UserIcon className="w-4 h-4" data-id="element-1689" data-mp="17wgo51umbxz" />
                      View Profile
                    </button>}
                </div>

                {/* Contact button (replaces Share) */}
                <button className={`p-2.5 rounded-lg border transition-colors ${dk.isDark ? 'border-gray-700 hover:bg-gray-800 text-gray-300' : 'border-gray-200 hover:bg-gray-50 text-gray-600'}`} title="Contact" data-id="element-1690" data-mp="3ngfkqsrcx0e9">
                  <MailIcon className="w-5 h-5" data-id="element-1691" data-mp="1qmu7cwm4gclv" />
                </button>

                {onDownload && <button onClick={onDownload} className={`p-2.5 rounded-lg border transition-colors ${dk.isDark ? 'border-gray-700 hover:bg-gray-800 text-gray-300' : 'border-gray-200 hover:bg-gray-50 text-gray-600'}`} title="Download" data-id="element-1692" data-mp="1p6ghoapdfhl1">
                    <DownloadIcon className="w-5 h-5" data-id="element-1693" data-mp="1b77a1zf8dvso" />
                  </button>}

                {/* Save Button Wrapper */}
                <div className={`flex items-center justify-center px-3 rounded-lg border ${dk.isDark ? 'border-gray-700' : 'border-gray-200'}`} data-id="element-1694" data-mp="27yj2zzcyjm1m">
                  <SaveToFavorites itemId={title} size="md" data-id="element-1695" data-mp="1y4sfawonnfmz" />
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>;
};
export default GraphicDetailCard;